import ApplicationServiceInterface from './ApplicationServiceInterface';
import {
  ApplicationNode,
  ApplicationBranch,
  ApplicationLeaf,
  PublicType,
  ApplicationTypeInfo,
} from '../model/ApplicationSwitcherModel';
import axios from '../../axios';

export interface ApplicationTreeData {
  items: (ApplicationBranchData | ApplicationLeafData)[];
}

export interface ApplicationLeafData {
  appId: string;
  appName: string;
  appSwitcherCompliance: boolean;
  createDefaultShortcut: boolean;
  id: string;
  hidden: boolean;
  label: string;
  shortcutLabel?: string;
  specialRole?: string;
  uri: string;
}

export interface ApplicationBranchData {
  children: ApplicationLeafData[];
  id: string;
  label: string;
  branch: boolean;
}

export interface TypesData {
  items: TypeData[];
}

export interface TypeData {
  apps: TypesAppData[];
  id: string;
}

export interface TypesAppData {
  appId: string;
  directive: string;
  targetVerbLabel: string;
  targetVerbCode?: string;
}

class AppRegistryService implements ApplicationServiceInterface {
  // TODO: Init by making call to AppRegistry Service root

  async getAvailableApps(): Promise<ApplicationNode[]> {
    let availableApplications: ApplicationNode[] = [];
    try {
      const data = await this.fetchApps();
      if (data && data.items) {
        availableApplications = this.createAppTreeModel(data.items);
      }
    } catch (err) {
      // Log error - unable to retrieve available applications
    }
    return availableApplications;
  }

  private async fetchApps(): Promise<ApplicationTreeData> {
    const url = '/appRegistry/applications/applicationTree';
    const response = await this.doGet(url);
    return response.data;
  }

  private async doGet(url: string): Promise<any> {
    const response = await axios.get(url);
    return response;
  }

  private createAppTreeModel(
    dataItems: (ApplicationBranchData | ApplicationLeafData)[]
  ): ApplicationNode[] {
    const appTreeModel: ApplicationNode[] = [];
    for (const item of dataItems) {
      appTreeModel.push(this.createNode(item));
    }
    return appTreeModel;
  }

  private createNode(item: ApplicationBranchData | ApplicationLeafData): ApplicationNode {
    if ((item as ApplicationBranchData).branch === true) {
      return this.createBranchNode(item as ApplicationBranchData);
    }
    return this.createLeaf(item as ApplicationLeafData);
  }

  private createBranchNode(item: ApplicationBranchData): ApplicationBranch {
    const children: ApplicationLeaf[] = [];
    for (const child of item.children) {
      children.push(this.createLeaf(child));
    }

    const branch: ApplicationBranch = {
      children,
      id: item.id,
      label: item.label,
    };
    return branch;
  }

  private createLeaf(item: ApplicationLeafData): ApplicationLeaf {
    const leaf: ApplicationLeaf = {
      appId: item.appId,
      appName: item.appName,
      appSwitcherCompliance: item.appSwitcherCompliance,
      createDefaultShortcut: item.createDefaultShortcut,
      directiveId: item.id,
      hidden: item.hidden,
      label: item.label,
      shortcutLabel: item.shortcutLabel,
      specialRole: item.specialRole,
      uri: item.uri,
    };
    return leaf;
  }

  async getPublicTypeSupportingApps() {
    let types: Map<string, PublicType> = new Map();
    try {
      const data = await this.fetchTypes();
      if (data && data.items) {
        types = this.createTypesModel(data.items);
      }
    } catch (err) {
      // Log error - unable to retrieve types
    }
    return types;
  }

  private async fetchTypes(): Promise<TypesData> {
    const url = '/appRegistry/types';
    const response = await this.doGet(url);
    return response.data;
  }

  private createTypesModel(items: TypeData[]) {
    const types: Map<string, PublicType> = new Map();
    for (const item of items) {
      types.set(item.id, this.createTypeApps(item.apps));
    }
    return types;
  }

  private createTypeApps(apps: TypesAppData[]): PublicType {
    const typeApps: ApplicationTypeInfo[] = [];
    for (const app of apps) {
      const typeApp = {
        appId: app.appId,
        directiveId: app.directive,
        verbLabel: app.targetVerbLabel,
        verbCode: app.targetVerbCode,
      };
      typeApps.push(typeApp);
    }
    return typeApps;
  }
}

export default AppRegistryService;
